export const ROUTE_FEATURES = Object.freeze({
  reels: 'reels',
  games: 'games',
  circles: 'circles',
  friends: 'friendships',
  discover: 'discovery',
  explore: 'discovery',
  search: 'discovery',
  saved: 'bookmarks',
  messages: 'messages',
  notifications: 'notifications',
});

const modes = [
  ['wall', 'Wall', 'house'],
  ['visual', 'Visual', 'image', 'visual-feed'],
  ['talk', 'Talk', 'message-circle', 'talk-feed'],
  ['circles', 'Circles', 'users', 'circles'],
  ['games', 'Games', 'grid-2x2', 'games'],
];

/** Keep route positions stable while making active view switching thumb reachable.
 * Non-feed routes retain ordinary link navigation; disabled modes never appear. */
export function bindThumbViews(
  ctx,
  onSelect,
  { doc = globalThis.document, win = globalThis.window } = {},
) {
  const trigger = doc.querySelector('#mobile-nav a');
  if (!trigger) return () => {};
  trigger.addEventListener('click', (event) => {
    if (
      !win.matchMedia('(max-width: 620px)').matches ||
      !['feed', 'learning', 'circles', 'games'].includes(ctx.route()[0])
    )
      return;
    event.preventDefault();
    const content = ctx.e('div', 'thumb-view-menu');
    const selected = ['circles', 'games'].includes(ctx.route()[0])
      ? ctx.route()[0]
      : ctx.experience;
    let close;
    for (const [value, label, icon, flag] of modes) {
      if (flag && !ctx.featureEnabled(flag)) continue;
      const choice = ctx.button(
        label,
        async () => {
          close?.();
          await onSelect(value);
        },
        'thumb-menu-choice palette-choice',
      );
      choice.prepend(ctx.icon(icon));
      choice.setAttribute('aria-pressed', String(value === selected));
      content.append(choice);
    }
    const search = doc.querySelector('#search-toggle');
    if (search && !search.hidden)
      content.append(
        ctx.button(
          'Search',
          () => {
            close?.();
            search.click();
          },
          'thumb-menu-choice',
        ),
      );
    if (ctx.featureEnabled('publishing'))
      content.append(
        ctx.button(
          'Share a moment',
          () => {
            close?.();
            ctx.composer();
          },
          'thumb-menu-choice',
        ),
      );
    close = ctx.contextMenu(trigger, 'Wall', content);
  });
  return function update() {
    const inView = ['feed', 'learning', 'circles', 'games'].includes(ctx.route()[0]);
    const selected = inView
      ? ['circles', 'games'].includes(ctx.route()[0])
        ? ctx.route()[0]
        : ctx.experience
      : 'wall';
    const [, label, icon] = modes.find(([value]) => value === selected) ?? modes[0];
    trigger.replaceChildren(ctx.icon(icon), ctx.e('span', '', ctx.uiText(label)));
    trigger.title = inView ? `${ctx.uiText(label)} · ${ctx.uiText('Options')}` : ctx.uiText(label);
    trigger.setAttribute('aria-label', trigger.title);
    if (inView) trigger.setAttribute('aria-haspopup', 'dialog');
    else {
      trigger.removeAttribute('aria-haspopup');
      trigger.removeAttribute('aria-expanded');
    }
  };
}

/** Restore each Circles route independently: opening a chat must not replace the
 * chat-list scroll memory. Actor changes discard all transient navigation positions. */
export function bindRouteContinuity(ctx, saveFeedPosition, { win = globalThis.window } = {}) {
  const positions = new Map();
  let previous = ctx.route().join('/');
  let actor;
  const changed = () => {
    const current = ctx.route().join('/');
    if (ctx.state) {
      if (actor !== ctx.state.me.id) positions.clear();
      actor = ctx.state.me.id;
      if (['feed', 'learning'].includes(ctx.previousPage)) saveFeedPosition();
      if (
        previous === 'circles' ||
        previous.startsWith('circles/') ||
        previous === 'explore' ||
        previous === 'games'
      )
        positions.set(previous, win.scrollY);
      ctx.render();
      win.scrollTo({
        top: ['feed', 'learning'].includes(ctx.route()[0])
          ? ctx.feedScroll
          : (positions.get(current) ?? 0),
        behavior: 'instant',
      });
    }
    previous = current;
  };
  win.addEventListener('hashchange', changed);
  return () => win.removeEventListener('hashchange', changed);
}
